import type { ReactNode } from 'react';
import CpmButton from './Button';

interface CpmInlineErrorBannerProps {
    message: string;
    onRetry?: () => void;
    retryLabel?: string;
    className?: string;
    /** Compact one-line style for dropdowns / tight slots. */
    compact?: boolean;
    children?: ReactNode;
}

/**
 * Inline shortfall-styled error with optional retry.
 * Matches destructive callout tokens used across CPM modals.
 */
export default function CpmInlineErrorBanner({
    message,
    onRetry,
    retryLabel = 'Try again',
    className = '',
    compact = false,
    children,
}: CpmInlineErrorBannerProps) {
    if (compact) {
        return (
            <div
                className={[
                    'flex items-start gap-[8px] px-[10px] py-[8px] rounded-[8px] bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)]',
                    className,
                ].join(' ')}
            >
                <span
                    className="flex-1 text-[var(--cpm-shortfall-text)]"
                    style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.45 }}
                >
                    {message}
                </span>
                {onRetry ? (
                    <button
                        type="button"
                        onClick={onRetry}
                        className="shrink-0 text-[var(--cpm-shortfall-text)] underline-offset-2 hover:underline"
                        style={{ fontSize: 12, fontWeight: 800 }}
                    >
                        {retryLabel}
                    </button>
                ) : null}
                {children}
            </div>
        );
    }

    return (
        <div className={['px-[20px] py-[40px] text-center', className].join(' ')}>
            <div
                className="inline-flex items-start text-left bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)]"
                style={{ gap: 10, padding: '12px 14px', borderRadius: 10, maxWidth: 480 }}
            >
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--cpm-shortfall-text)"
                    strokeWidth="2"
                    className="shrink-0 mt-px"
                >
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                    <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
                </svg>
                <div className="flex flex-col" style={{ gap: 10 }}>
                    <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-shortfall-text)]">
                        {message}
                    </p>
                    {onRetry ? (
                        <div>
                            <CpmButton variant="outline" size="sm" onClick={onRetry}>
                                {retryLabel}
                            </CpmButton>
                        </div>
                    ) : null}
                    {children}
                </div>
            </div>
        </div>
    );
}
